import { useState, useEffect, createContext, useContext, Suspense, lazy, Component } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/i18n";
import { ThemeProvider } from "@/themes/themes";

// Lazy load IntroVideoPage to prevent blocking render
const IntroVideoPage = lazy(() => import("@/pages/IntroVideoPage"));

// Regular page imports
import WelcomePage from "@/pages/WelcomePage";
import IntroPage from "@/pages/IntroPage";
import TutorialPage from "@/pages/TutorialPage";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import CaregiverPage from "@/pages/CaregiverPage";
import CaregiverPortalPage from "@/pages/CaregiverPortalPage";
import TrustedSitesPage from "@/pages/TrustedSitesPage";
import HistoryPage from "@/pages/HistoryPage";
import ResearchPage from "@/pages/ResearchPage";
import IncidentPage from "@/pages/IncidentPage";
import AdminDashboard from "@/pages/AdminDashboard";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import FamilyAcceptPage from "@/pages/FamilyAcceptPage";
import useServerWake from "@/hooks/useServerWake";

// ErrorBoundary for catching render errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0A0A0A',
          color: '#fff',
          padding: '20px'
        }}>
          <h2 style={{ marginBottom: '10px' }}>Something went wrong</h2>
          <p style={{ opacity: 0.7, marginBottom: '20px' }}>Please refresh the page</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: 'var(--havn-primary)',
              color: '#000',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Loading fallback
const LoadingFallback = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--havn-bg)',
    color: 'rgba(255,255,255,0.6)'
  }}>
    Loading...
  </div>
);

// ProtectedRoute — requires authentication
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingFallback />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// OnboardingRoute — requires auth, part of first-run flow
function OnboardingRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingFallback />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Session Context for global state
export const SessionContext = createContext(null);

// Session log utility
const createSessionLogger = () => {
  const getStoredLogs = () => {
    try {
      return JSON.parse(localStorage.getItem('havn_session_logs') || '[]');
    } catch {
      return [];
    }
  };

  const saveLogs = (logs) => {
    const trimmed = logs.slice(-500);
    localStorage.setItem('havn_session_logs', JSON.stringify(trimmed));
  };

  return {
    log: (type, data = {}) => {
      const logs = getStoredLogs();
      logs.push({
        timestamp: new Date().toISOString(),
        type,
        ...data
      });
      saveLogs(logs);
    },
    getLogs: getStoredLogs,
    clear: () => localStorage.removeItem('havn_session_logs'),
    export: () => {
      const logs = getStoredLogs();
      const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `havn-logs-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };
};

// Conversation storage utility
const createConversationStorage = () => {
  const getConversations = () => {
    try {
      return JSON.parse(localStorage.getItem('havn_conversations') || '[]');
    } catch {
      return [];
    }
  };

  const saveConversations = (convos) => {
    const trimmed = convos.slice(-50);
    localStorage.setItem('havn_conversations', JSON.stringify(trimmed));
  };

  return {
    add: (role, content, sessionId) => {
      const convos = getConversations();
      convos.push({
        id: `msg_${Date.now()}`,
        timestamp: new Date().toISOString(),
        role,
        content,
        sessionId
      });
      saveConversations(convos);
    },
    getAll: getConversations,
    clear: () => localStorage.removeItem('havn_conversations')
  };
};

// Caregiver storage utility
const createCaregiverStorage = () => {
  const getCaregiver = () => {
    try {
      return JSON.parse(localStorage.getItem('havn_caregiver') || 'null');
    } catch {
      return null;
    }
  };

  return {
    get: getCaregiver,
    save: (caregiver) => {
      localStorage.setItem('havn_caregiver', JSON.stringify(caregiver));
    },
    clear: () => localStorage.removeItem('havn_caregiver')
  };
};

export const useSession = () => useContext(SessionContext);

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, profile } = useAuth();

  // Silent server wake-up ping — warms Railway before user interacts
  useServerWake();

  // Initialize utilities
  const [sessionLogger] = useState(() => createSessionLogger());
  const [conversationStorage] = useState(() => createConversationStorage());
  const [caregiverStorage] = useState(() => createCaregiverStorage());

  // Check first launch status
  const [isFirstLaunch, setIsFirstLaunch] = useState(() => {
    return localStorage.getItem('havn_first_run') !== 'complete';
  });

  const [tutorialCompleted, setTutorialCompleted] = useState(() => {
    return localStorage.getItem('havn_tutorial_completed') === 'true';
  });

  // Check if intro video has been seen
  const [introVideoSeen] = useState(() => {
    return localStorage.getItem('havn_intro_seen') === 'true';
  });

  // Session state
  const [currentSessionId, setCurrentSessionId] = useState(() =>
    `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  );
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [lastInteractionTime, setLastInteractionTime] = useState(Date.now());
  const [conversationCount, setConversationCount] = useState(0);

  // Mark first run complete
  const completeFirstRun = () => {
    localStorage.setItem('havn_first_run', 'complete');
    setIsFirstLaunch(false);
  };

  // Mark tutorial complete
  const completeTutorial = () => {
    localStorage.setItem('havn_tutorial_completed', 'true');
    setTutorialCompleted(true);
    completeFirstRun();
  };

  // Start new session
  const startNewSession = () => {
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setCurrentSessionId(newSessionId);
    setSessionStartTime(Date.now());
    setLastInteractionTime(Date.now());
    setConversationCount(0);
    sessionLogger.log('session_start', { sessionId: newSessionId });
    return newSessionId;
  };

  // Check if session should end based on context
  const checkSessionEnd = (message, response) => {
    const closingPhrases = [
      // English
      "that's all", "thats all", "thank you", "thanks", "goodbye", "bye",
      "that will be all", "i'm done", "im done", "nothing else", "no more",
      // French (Quebec/Canadian). `includes` is substring-based, so shorter
      // entries like "merci" will match "merci beaucoup" etc.
      "merci", "au revoir", "bonne journée", "bonne soirée",
      "c'est tout", "c'est tout merci", "j'ai fini", "rien d'autre",
      "à bientôt", "bonne nuit",
    ];

    const msgLower = message.toLowerCase();
    const responseLower = (response || '').toLowerCase();
    // Check BOTH sides: the user's message AND HAVN's reply. If HAVN
    // produced a farewell (e.g. "passez une belle journée", "stay safe
    // online") in response to a closing gesture, that's also a signal
    // the conversation is winding down.
    const hasClosingPhrase = closingPhrases.some(phrase =>
      msgLower.includes(phrase) || responseLower.includes(phrase)
    );
    // Extra FR/EN farewell patterns that only make sense when HAVN
    // says them, not the user (user saying "stay safe online" is weird).
    const assistantFarewellPatterns = [
      "stay safe online",
      "have a good day", "have a nice day", "have a great day",
      "passez une belle journée", "passez une bonne journée",
      "bonne journée à vous", "naviguez prudemment",
    ];
    const assistantSaidFarewell = assistantFarewellPatterns.some(phrase =>
      responseLower.includes(phrase)
    );

    if (hasClosingPhrase || assistantSaidFarewell) {
      sessionLogger.log('session_end_detected', {
        reason: 'closing_phrase',
        source: assistantSaidFarewell && !hasClosingPhrase ? 'assistant' : 'user',
        sessionId: currentSessionId,
        turnCount: conversationCount
      });
      return true;
    }

    return false;
  };

  // Update interaction time
  const recordInteraction = () => {
    setLastInteractionTime(Date.now());
    setConversationCount(prev => prev + 1);
  };

  // Auto-session timeout (5 minutes of inactivity)
  useEffect(() => {
    const checkTimeout = setInterval(() => {
      const timeSinceInteraction = Date.now() - lastInteractionTime;
      if (timeSinceInteraction > 5 * 60 * 1000 && conversationCount > 0) {
        sessionLogger.log('session_end_timeout', {
          sessionId: currentSessionId,
          turnCount: conversationCount
        });
        startNewSession();
      }
    }, 60000);

    return () => clearInterval(checkTimeout);
  }, [lastInteractionTime, conversationCount, currentSessionId]);

  // Route guard for first launch (authenticated users only)
  useEffect(() => {
    if (user && isFirstLaunch && location.pathname === '/home') {
      navigate('/welcome', { replace: true });
    }
  }, [user, isFirstLaunch, location.pathname, navigate]);

  // Sync onboarding state from profile
  useEffect(() => {
    if (profile?.onboarding_complete) {
      if (isFirstLaunch) {
        localStorage.setItem('havn_first_run', 'complete');
        setIsFirstLaunch(false);
      }
    }
  }, [profile]);

  // Context value
  const sessionContextValue = {
    currentSessionId,
    startNewSession,
    recordInteraction,
    checkSessionEnd,
    conversationCount,
    sessionLogger,
    conversationStorage,
    caregiverStorage,
    isFirstLaunch,
    tutorialCompleted,
    completeFirstRun,
    completeTutorial
  };

  // Show loading while auth initializes
  if (authLoading) return <LoadingFallback />;

  return (
    <SessionContext.Provider value={sessionContextValue}>
      <ErrorBoundary>
        <Routes>
          {/* PUBLIC routes — no auth required */}
          <Route path="/login" element={
            user ? <Navigate to="/home" replace /> : <LoginPage />
          } />
          <Route path="/signup" element={
            user ? <Navigate to="/home" replace /> : <SignupPage />
          } />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/family/accept" element={<FamilyAcceptPage />} />

          {/* ONBOARDING routes — auth required */}
          <Route path="/intro-video" element={
            <OnboardingRoute>
              <ErrorBoundary fallback={<Navigate to="/welcome" replace />}>
                <Suspense fallback={<LoadingFallback />}>
                  <IntroVideoPage />
                </Suspense>
              </ErrorBoundary>
            </OnboardingRoute>
          } />
          <Route path="/welcome" element={<OnboardingRoute><WelcomePage /></OnboardingRoute>} />
          <Route path="/intro" element={<OnboardingRoute><IntroPage /></OnboardingRoute>} />
          <Route path="/tutorial" element={<OnboardingRoute><TutorialPage /></OnboardingRoute>} />

          {/* PROTECTED routes — auth + onboarding required */}
          <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
          <Route path="/caregiver" element={<ProtectedRoute><CaregiverPage /></ProtectedRoute>} />
          <Route path="/caregiver-portal" element={<ProtectedRoute><CaregiverPortalPage /></ProtectedRoute>} />
          <Route path="/trusted-sites" element={<ProtectedRoute><TrustedSitesPage /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
          <Route path="/research" element={<ProtectedRoute><ResearchPage /></ProtectedRoute>} />
          <Route path="/incident" element={<ProtectedRoute><IncidentPage /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminDashboard />} />

          {/* ROOT redirect logic */}
          <Route path="/" element={
            !user ? <Navigate to="/login" replace /> :
            !introVideoSeen ? <Navigate to="/intro-video" replace /> :
            isFirstLaunch ? <Navigate to="/welcome" replace /> :
            <Navigate to="/home" replace />
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    </SessionContext.Provider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
